import clsx from "clsx";
import { motion, Variants } from "framer-motion";
import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  useClickAway,
  useLockBodyScroll,
  usePrevious,
  useThrottle,
  useWindowScroll,
} from "react-use";

import { useHeaderMenu } from "../../../hooks/use-header-menu";
import { HeaderMenuSpecialItems } from "../types";
import { HeaderMenuItemTab, HeaderMenuItemTabs } from "./header-menu-item-tabs";
import { HeaderMenuMain } from "./header-menu-main";
import {
  LocaleSelectorSpecialItem,
  PersonalAreaSpecialItem,
  SearchSpecialItem,
} from "./header-menu-special-items";

export interface HeaderMenuDesktopProps {
  className?: string;
  node_locale: string;
}

const commonSpecialItemClassName = "xl:container !max-w-[1440px] mx-auto";

interface CustomData {
  isItemPanelExpanded: boolean;
}

const variants: Variants = {
  initial: { y: "-100%", opacity: 0 },
  animate: ({ isItemPanelExpanded }: CustomData) => ({
    y: isItemPanelExpanded ? 0 : "-100%",
    opacity: isItemPanelExpanded ? 1 : 0,
  }),
  exit: ({ isItemPanelExpanded }: CustomData) =>
    isItemPanelExpanded ? {} : { y: "-100%", opacity: 0 },
};

export const HeaderMenuDesktop: FC<HeaderMenuDesktopProps> = ({ className, node_locale }) => {
  const mainRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const { expandedItemId, expandedEditorialItem, setItemPanelExpanded, isItemPanelExpanded } =
    useHeaderMenu();

  useLockBodyScroll(isItemPanelExpanded);

  const rootRef = useRef<HTMLDivElement>(null);
  useClickAway(rootRef, () => {
    setItemPanelExpanded(false);
  });

  const threshold = 2;
  const { y } = useWindowScroll();
  const previousScroll = usePrevious(y);
  const [shouldShowMenu, setShouldShowMenu] = useState(true);
  useEffect(() => {
    const deltaY = y - (previousScroll ?? 0);
    if (expandedItemId || deltaY < -threshold) {
      setShouldShowMenu(true);
    } else if (deltaY > threshold) {
      setShouldShowMenu(false);
    }
  }, [y, previousScroll, expandedItemId]);

  const shouldShowMenuThrottled = useThrottle(shouldShowMenu, 200);

  const renderedSpecialItems: Record<HeaderMenuSpecialItems, ReactNode> = {
    [HeaderMenuSpecialItems.LocaleSelector]: (
      <LocaleSelectorSpecialItem className={commonSpecialItemClassName} />
    ),
    [HeaderMenuSpecialItems.PersonalArea]: (
      <PersonalAreaSpecialItem className={commonSpecialItemClassName} node_locale={node_locale} />
    ),
    [HeaderMenuSpecialItems.Search]: <SearchSpecialItem className={commonSpecialItemClassName} />,
  };

  const scrollableClassName = "overflow-y-auto menu-scrollbar";
  const [maxPanelHeight, setMaxPanelHeight] = useState(0);
  const [mainHeight, setMainHeight] = useState(0);
  const calculateMaxPanelHeight = useCallback(() => {
    if (typeof window !== "undefined") {
      const mainHeight = mainRef.current?.getBoundingClientRect().height ?? 0;
      setMainHeight(mainHeight);
      const tabsHeight = tabsRef.current?.getBoundingClientRect().height ?? 0;
      const maxHeight = window.innerHeight - tabsHeight - mainHeight;
      if (Math.abs(maxHeight) !== Infinity) {
        setMaxPanelHeight(maxHeight);
      }
      return requestAnimationFrame(() => {
        calculateMaxPanelHeight();
      });
    }
  }, []);

  useEffect(() => {
    const requestId = calculateMaxPanelHeight();
    return () => {
      if (requestId !== undefined) {
        cancelAnimationFrame(requestId);
      }
    };
  }, [calculateMaxPanelHeight]);

  return (
    <div
      ref={rootRef}
      className={clsx(
        "overflow-hidden hidden z-[100] lg:flex flex-col",
        "sticky w-full top-0 transition-[transform] duration-200",
        !shouldShowMenuThrottled && "translate-y-[-100%]",
        isItemPanelExpanded ? "border-b-0" : "border-b",
        "border-gray-300 duration-300",
        className
      )}
    >
      <HeaderMenuMain ref={mainRef} />

      <div
        className={clsx("w-full fixed", isItemPanelExpanded ? "max-h-screen" : "max-h-0")}
        style={{ top: mainHeight }}
      >
        <motion.div
          className={clsx(
            "bg-neutral relative rounded-b-4xl overflow-hidden",
            isItemPanelExpanded && "shadow border-b border-neutral-dark"
          )}
          key={expandedItemId}
          custom={{ isItemPanelExpanded }}
          transition={{ ease: "easeOut", duration: 0.2 }}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {expandedItemId &&
            (expandedEditorialItem ? (
              <>
                <HeaderMenuItemTabs ref={tabsRef} className="z-20 sticky top-0" />
                <HeaderMenuItemTab
                  className={scrollableClassName}
                  style={{ maxHeight: maxPanelHeight }}
                />
              </>
            ) : (
              <div
                className={clsx("p-10 pt-4", scrollableClassName)}
                style={{ maxHeight: maxPanelHeight }}
              >
                {renderedSpecialItems[expandedItemId as HeaderMenuSpecialItems]}
              </div>
            ))}
        </motion.div>
      </div>
    </div>
  );
};
